import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom'
import { useMutation } from 'react-query'
import NProgress from 'nprogress'
import { toast } from 'react-hot-toast'

import { PATH_SIGN_IN } from './SignInPage'
import { Input } from 'components/Input'
import { AuthLayout } from 'components/auth/AuthLayout'
import { Button } from 'components/Button'
import { PATH_INVITE_WHITELIST } from './InviteWhitelistPage'
import { AxiosError } from 'axios'
import { useApi } from 'contexts/di-context'
import { ApiError } from 'api/models'

export const PATH_RECOVERY_TOKEN = '/recovery-token'

export const RecoveryTokenPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const location = useLocation()
  const api = useApi()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState(searchParams.get('email') || location.state?.email || '')

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) =>
    setEmail(event.target.value)

  const postRecoveryTokenMutation = useMutation(() => api.postRecoveryToken({ email }), {
    onSuccess: () => {
      NProgress.done()
      toast.success(
        <span>
          <span className="mr-[16px]">{t('auth.recoveryTokenPage.success', { email })}</span>
          <a
            href="https://www.productscience.ai/contact"
            target="_blank"
            rel="noopener"
            className="font-medium"
          >
            {t('auth.common.contactUs')}
          </a>
        </span>,
      )
      navigate(PATH_SIGN_IN, { replace: true })
    },
    onError(error: AxiosError<ApiError>) {
      NProgress.done()
      toast.error(error.response?.data.message ?? t('errorMessage'))
    },
  })

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault()
    NProgress.start()
    postRecoveryTokenMutation.mutate()
  }

  return (
    <AuthLayout
      bottomInfo={[
        <Link to={PATH_SIGN_IN} state={{ email }}>
          {t('auth.common.iRememberMyPassword')}
        </Link>,
        <span>
          <span className="text-gray-normal">{t('auth.common.signUpText')}</span>{' '}
          <Link to={PATH_INVITE_WHITELIST}>{t('auth.common.register')}</Link>
        </span>,
      ]}
    >
      <h2 className="text-center text-small tracking-wide text-gray-normal">
        {t('auth.recoveryTokenPage.header')}
      </h2>
      <form className="mt-6" onSubmit={handleSubmit}>
        <div>
          <label htmlFor="email-address" className="sr-only">
            {t('auth.common.email')}
          </label>
          <Input
            value={email}
            onChange={handleEmailChange}
            id="email-address"
            name="email"
            type="email"
            autoComplete="email"
            placeholder={t('auth.common.email')}
            required
          />
        </div>
        <Button className="w-full" disabled={postRecoveryTokenMutation.isLoading} isSmall>
          {t('auth.recoveryTokenPage.button')}
        </Button>
      </form>
    </AuthLayout>
  )
}
